import {useState, useEffect, useMemo} from 'react';
import * as XLSX from 'xlsx';
import {fetchAbnormalSummary} from '@/services/api';
import {normalizeProvinceKey, toProvinceDisplayName} from '@/utils/provinces.ts';
import ChinaMap from './ChinaMap';
import HospitalModal from './HospitalModal';
import type {AbnormalSummary, AbnormalHospital} from '@/types';
import {useAppStore} from '@/store/appStore.ts';
import {formatBatch} from "@/utils/format.ts";
import {Spinner} from "@/components/common/Spinner.tsx";
import {HospitalItem} from "@/pages/abnormal/HospitalItem.tsx";


export default function AbnormalSalesApp() {
    const selectBatch = useAppStore((s) => s.selectBatch);
    const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
    const [selectedPlatform, setSelectedPlatform] = useState('BIO');
    const [selectedHospitalCode, setSelectedHospitalCode] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [summary, setSummary] = useState<AbnormalSummary | null>(null);
    const [loading, setLoading] = useState(true);

    // Load data when platform or batch changes
    useEffect(() => {
        if (!selectBatch) return;
        let alive = true;
        setLoading(true);

        fetchAbnormalSummary({platform: selectedPlatform, batch:selectBatch.id})
            .then((data) => {
                if (alive) setSummary(data);
            })
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => {
            alive = false;
        };
    }, [selectedPlatform, selectBatch]);

    const platforms = summary?.platforms?.length ? summary.platforms : ['BIO'];
    const hospitals = summary?.hospitals ?? [];
    const highCount = hospitals.filter((h) => h.iraiLevel === 'High').length;

    const filteredHospitals = useMemo(() => {
        let rows = hospitals;
        if (selectedProvince) rows = rows.filter((h) => normalizeProvinceKey(h.province) === selectedProvince);
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            rows = rows.filter((h) => h.name.toLowerCase().includes(q) || h.hospitalCode.toLowerCase().includes(q));
        }
        return rows;
    }, [hospitals, selectedProvince, searchQuery]);

    const exportExcel = () => {
        if (!summary) return;
        const data = summary.hospitals.map((h) => ({
            '医院编号': h.hospitalCode,
            '医院名称': h.name,
            '省份': h.province,
            'segment': h.segment,
            '销售大区': h.rsm,
            '销售小区': h.dsm,
            '评级月份': summary.batch,
            'IRAI分数': h.iraiScore,
            '评级结果': h.iraiLevel,
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        const buf = XLSX.write(wb, {bookType: 'xlsx', type: 'array'});
        const blob = new Blob([buf], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${summary.batch}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div style={{
            display: 'flex', height: '100%', background: '#f8fafc',
            overflow: 'hidden',
        }}>
            {/* Map */}
            <div style={{flex: 1, padding: '10px 5px 10px 10px', display: 'flex', minWidth: 0}}>
                <div style={{
                    flex: 1, background: 'white', borderRadius: '0 10px 10px 0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: 14,
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 10
                    }}>
                        <span style={{fontSize: 13, fontWeight: 600, color: '#1e293b'}}>Map View</span>
                        <div style={{display: 'flex', gap: 6}}>
                            {platforms.map((p) => (
                                <button key={p} onClick={() => setSelectedPlatform(p)}
                                        style={{
                                            padding: '4px 10px', borderRadius: 5,
                                            border: selectedPlatform === p ? '1px solid #dc2626' : '1px solid #e2e8f0',
                                            background: selectedPlatform === p ? '#fef2f2' : 'white',
                                            color: selectedPlatform === p ? '#dc2626' : '#64748b',
                                            fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                        }}>
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div style={{flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0}}>
                        {loading &&
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%'
                            }}>
                                <Spinner size={40}/>
                            </div>}
                        {!loading &&
                            <ChinaMap
                                provinceSummary={summary?.provinceSummary ?? []}
                                selectedProvince={selectedProvince}
                                onProvinceClick={setSelectedProvince}
                            />
                        }
                    </div>
                </div>
            </div>

            {/* Hospital list */}
            <div style={{
                width: 376,
                background: '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                padding: '10px 10px 10px 5px'
            }}>
                <div style={{
                    flex: 1, background: 'white', borderRadius: '10px 0 0 10px',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                    <div style={{padding: '14px 18px', borderBottom: '1px solid #e2e8f0', flexShrink: 0}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10}}>
                            <h2 style={{fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0}}>Abnormal Sales</h2>
                            <span style={{
                                padding: '3px 10px',
                                background: '#fef2f2',
                                color: '#dc2626',
                                fontSize: 11,
                                fontWeight: 600,
                                borderRadius: 10
                            }}>
                                {filteredHospitals.filter((h) => h.iraiLevel === 'High').length} HSPs with High Abnormal Sales
                            </span>
                        </div>
                        <input
                            type="text"
                            placeholder="Please enter a hospital name to search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%', padding: '7px 12px', fontSize: 12,
                                border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none',
                                color: '#1e293b', background: '#f8fafc', fontFamily: 'inherit',
                            }}
                        />
                        <div style={{marginTop: 5, display: 'flex', alignItems: 'center', gap: 8}}>
                            <button onClick={exportExcel}
                                    style={{
                                        padding: '3px 8px',
                                        borderRadius: 5,
                                        border: '1px solid #e2e8f0',
                                        background: '#f8fafc',
                                        color: '#1e293b',
                                        fontSize: 11,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        fontFamily: 'inherit',
                                    }}>
                                {'下载xlsx'}
                            </button>
                            {selectedProvince && <>
                                <span style={{
                                    padding: '3px 8px',
                                    background: '#e0f2fe',
                                    color: '#0369a1',
                                    fontSize: 11,
                                    fontWeight: 500,
                                    borderRadius: 4
                                }}>
                                  {toProvinceDisplayName(selectedProvince)}
                                </span>
                                <button onClick={() => setSelectedProvince(null)}
                                        style={{
                                            padding: '3px 6px',
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#64748b',
                                            fontSize: 11,
                                            cursor: 'pointer',
                                            fontFamily: 'inherit'
                                        }}>
                                    Clear
                                </button>
                            </>}
                            <div style={{marginTop: 8, fontSize: 11, color: '#94a3b8', marginLeft: 'auto'}}>
                                Data Update Time: {formatBatch(summary?.batch ?? '')}
                            </div>
                        </div>
                    </div>

                    <div style={{flex: 1, overflowY: 'auto', padding: '12px 18px', minHeight: 0}}>
                        {loading ? (
                            <div style={{textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 12}}>Loading
                                hospitals...</div>
                        ) : filteredHospitals.length ? (
                            filteredHospitals.map((h) => (
                                <HospitalItem key={h.hospitalCode} hospital={h}
                                              onClick={() => setSelectedHospitalCode(h.hospitalCode)}/>
                            ))
                        ) : (
                            <div style={{textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 12}}>No hospitals
                                found</div>
                        )}
                    </div>
                </div>
            </div>

            {selectedHospitalCode && summary && (
                <HospitalModal hospitalCode={selectedHospitalCode} batch={summary.batch}
                               onClose={() => setSelectedHospitalCode(null)}/>
            )}
        </div>
    );
}
